import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {
  BrokerApiContractAccountDataModel,
  brokerApiContractAccountReturnDataFactory,
  BrokerContractAccountReturnDataModel,
  createContractAccountKeys,
  ItsmContractAccountDataFactory,
  ItsmContractAccountDataModel,
  ItsmContractAccountReturnDataModel,
  MasterAgreementStatus,
  projectGqlModelFactory,
  ProjectModel,
  projectModelFactory,
  projectUpdateKeys,
  TransactionResponse,
} from "./project.model";
import {ProjectDetailListResponse, projectDetailListResponseFactory} from "./project-detail.model";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";
import {pick} from "lodash";
import {ItsmAddressesForProjectModel, itsmAddressesForProjectModelFactory} from "../address/itsm-address.model";
import {ItsmContactModel, itsmContactModelListFactory} from "../contact/itsm-contact.model";
import {Apollo} from "apollo-angular";
import {CustomLabelsUpdateGqlModel, FleetCreationGqlModel} from "../customer/customer.gql-model";
import {UpdateContractAccountGqlModel, UpdateContractAccountInputResponseGqlModel} from "../contract-account/contract-account.gql-model";
import gql from "graphql-tag";
import { PrivilegeViewService } from "../privilege-view/privilege-view.service";
import {HasuraGraphqlService} from "../graphql/hasura-graphql-service";
import {Query} from "../graphql/query-constant";

const DEFAULT_PAGE = "1";
const DEFAULT_PAGE_LIMIT = "10";

@Injectable()
export class ProjectService {
  private url: string = "/api/projects";

  constructor(
    public http: HttpClient,
    private apollo: Apollo,
    private privilegeViewService: PrivilegeViewService,
    private hasuraGqlService: HasuraGraphqlService,
  ) {}

  projectVariables(id: string, domain: string) {
    if (domain && id) return {id, domain};
    if (id) return {id};
    return {id: undefined};
  }

  getProjectDetails(id = "", params) {
    const query: any = gql`
    query getProject($id: ID!, $domain: String!) 
    {
      projects: projects(id: $id, domain: $domain) { 
        ${params}
      }
    }
    `;
    const domain = this.privilegeViewService.getDomainView();
    const variables = this.projectVariables(id, domain);
    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(map(projectGqlModelFactory))
      .toPromise();
  }

  getProjects(
    page: string = DEFAULT_PAGE,
    limit: string = DEFAULT_PAGE_LIMIT,
    search: string = "",
    limitByRegion: boolean = false,
    closed: boolean = false,
    includeMpcProjects: boolean = false,
    onlyMpcProjects: boolean = false,
    showAllProjects: boolean = false,
    filterTargetSystem: string = "",
    sortBy: boolean = false,
    type: string = "",
    domain: string = "cusReseller",
  ): Promise<ProjectDetailListResponse> {
    const params = {
      page,
      limit,
      search,
      limitByRegion: `${limitByRegion}`,
      closed: `${closed}`,
      includeMpcProjects: `${includeMpcProjects}`,
      onlyMpcProjects: `${onlyMpcProjects}`,
      sequence: "true",
      showAllProjects: `${showAllProjects}`,
      filterTargetSystem: `${filterTargetSystem}`,
      sortBy: `${sortBy}`,
      type: `${type}`,
      domain: `${domain}`,
    };
    return this.http.get<ProjectDetailListResponse>(`${this.url}`, {params})
      .pipe(map(projectDetailListResponseFactory))
      .toPromise();
  }

  getProjectById(id = ""): Observable<ProjectModel> {
    return this.http.get<ProjectModel>(`${this.url}/id?id=${id}`)
      .pipe(map(projectModelFactory));
  }

  saveProject(project: ProjectModel,  extras: any = {}): Promise<{project: ProjectModel, transactionId: string, customLabelsTransactionId: string}> {
    const body = {...pick(project, projectUpdateKeys), extras};
    return this.http.post<{project: ProjectModel, transactionId: string, customLabelsTransactionId: string}>(`${this.url}/save`, body)
      .pipe(map(({project: p, transactionId, customLabelsTransactionId}) => ({project: projectModelFactory(p), transactionId, customLabelsTransactionId})))
      .toPromise();
  }

  getFleetStatus(transactionId): Observable<FleetCreationGqlModel> {
    const query = FleetCreationGqlModel.query;
    const variables = FleetCreationGqlModel.variables(transactionId);

    return this.apollo.subscribe({query, variables, fetchPolicy: "no-cache"})
      .pipe(FleetCreationGqlModel.mapper);
  }

  updateFleetId(projectId) {
    return this.http.put(`${this.url}/update-fleet-id`, projectId).toPromise();
  }

  getCustomLabelsUpdateStatus(transactionId): Observable<CustomLabelsUpdateGqlModel> {
    const query = CustomLabelsUpdateGqlModel.query;
    const variables = CustomLabelsUpdateGqlModel.variables(transactionId);

    return this.apollo.subscribe({query, variables, fetchPolicy: "no-cache"})
      .pipe(CustomLabelsUpdateGqlModel.mapper);
  }

  getListAllowedContractAccounts(projectId: string): Promise<BrokerContractAccountReturnDataModel> {
    return this.http.get<BrokerContractAccountReturnDataModel>(`${this.url}/getListAllowedContractAccounts?projectId=${projectId}`)
      .pipe(map(brokerApiContractAccountReturnDataFactory))
      .toPromise();
  }

  createContractAccount(params: BrokerApiContractAccountDataModel): Promise<{filename: string}> {
    const body = {...pick(params, createContractAccountKeys)};
    return this.http.post<{filename: string}>(`${this.url}/create-contract-account`, body)
      .pipe(map(({filename}) => ({filename})))
      .toPromise();
  }

  updateContractAccount(caName, pendingCaName?, projectId?, status?):
   Promise<UpdateContractAccountInputResponseGqlModel> {
    const model = new UpdateContractAccountGqlModel({caName, pendingCaName, projectId, status});

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(UpdateContractAccountGqlModel.mapper)
      .toPromise();
  }

  saveShippingLabels(projectId, shippingLabel1, shippingLabel2): Promise<boolean> {
    return this.http.post(`${this.url}/save-shipping-label`, {projectId, shippingLabel1, shippingLabel2})
      .pipe(map(json => !!json["success"]))
      .toPromise();
  }

  updateGeoRegion(projectId, geoRegionId): Promise<boolean> {
    return this.http.post(`${this.url}/update-field`, {id: projectId, paramName: "geo_region_id", value: geoRegionId})
      .pipe(map(json => !!json["success"]))
      .toPromise();
  }

  getDartOffers(projectId): Promise<any> {
    const params = {projectId};
    return this.http.get(`${this.url}/get-dart-offers`, {params}).toPromise();
  }
  
  saveDeliveryOpsInfo(project: any): Observable<any> {
    const projectKeys = [
      "multiYearPoType",
      "multiYearTermDuration",
      "prepaidFieldOnDorTab",
      "previousDocument",
      "renewal",
      "legacyReportedDate",
      "legacyReportedThruDate",
      "legacyDocumentType",
      "legacyDocumentNumber",
      "endCustomerSplit",
      "payerSplit",
      "poNumberSplit",
      "shipToSplit",
      "id",
      "csa",
      "ampId",
      "msId",
      "orderAcceptance",
      "customerErpNumber",
      "opgNumber",
      "saLetter",
      "initialBillingDate",
      "billingStart",
      "enableCatalogPricing",
      "orderAcceptanceDisableReason", // Only used on disabling orderAcceptance
      "orderAcceptanceCompleteDate",
      "defaultContractAccountId",
      // SIMPLIFY PARAMETERS
      "vendorContractAccNumber",
      "billingModelSplit",
      "businessModelSplit",
      "chargeTypeSplit",
      "maStartDate",
      "billingCycle",
      "billingFrequency",
      "commercialityCode",
      "globalDeal",
      "globalDealClassification",
      "podRequired",
      "endUserSplit",
      "titleStatusSplit",
      "billToAddressId",
      "soldToAddressId",
      "payerAddressId",
      "endUserAddressId",
      "endCustomerAddressId",
      "billToContactId",
      "invoiceContactId",
      "hpInvoiceContactId",
      "hpSalesRepContactId",
      "hpContractAdminContactId",
      "hpAccDeliveryMgrContactId",
      "coTerminousDate",
      "purchaseOrderSplit",
      "salesOrganization",
      "supplyRoutingId",
      "billFromStructure",
      "billFromBPIDId",
      "contractAccounts",
      "productsNeedingExport"
    ];

    return this.http.post(`${this.url}/save-delivery-ops-info`, {
      ...pick(project, projectKeys),
    })
      .pipe(map((json) => (json)));
  }

  saveInvoiceSplitInfo(project: any): Observable<any> {
    const projectKeys = [
      "endUserSplit",
      "shipToSplit",
      "id",
      "billingModelSplit",
      "businessModelSplit",
      "chargeTypeSplit",
      "purchaseOrderSplit",
      "titleStatusSplit",
      "opgNumber",
    ];

    return this.http.post(`${this.url}/save-invoice-split-info`, {
      ...pick(project, projectKeys),
    })
      .pipe(map((json) => (json)));
  }

  sendSaLetterToITSM(saLetter, projectId): Promise<any> {
    return this.http.post(`${this.url}/save-saletter-itsm`, {saLetter, projectId})
      .pipe(map(json => json["success"]))
      .toPromise();
  }

  saveCustomerFleet(id, customerId, customerName): Observable<string> {
    return this.http.post(`${this.url}/save-customer-fleet`, {id, customerId, customerName})
      .pipe(map(() => id));
  }

  unassignCustomer(id): Observable<any> {
    return this.http.post(`${this.url}/unassign-customer`, {id});
  }

  remove(id): Observable<any> {
    return this.http.post(`${this.url}/remove`, {id});
  }

  create(name: string, region?: string, targetSystem?: string, country?: string): Observable<string> {
    // Get the value from local storage
    const domain = this.privilegeViewService.getDomainView();
    return this.http.post(`${this.url}/create`, {name, region, targetSystem, domain, country})
      .pipe(map(result => `${result}`));
  }

  assignUserToProject(projectId: string): Observable<any> {
    return this.http.post(`${this.url}/assign-user-to-project`, {projectId});
  }

  unAssignUserToProject(projectId: string): Observable<any> {
    return this.http.post(`${this.url}/unassign-user-to-project`, {projectId});
  }

  getAssignableSubstituteUsers(domain): Observable<any> {
    return this.http.post(`${this.url}/get-assignable-sub-users`, {domain});
  }

  crossProductUserProjectAssignment(projectIds, userIds): Observable<any> {
    return this.http.post(`${this.url}/assign-users-to-projects`, {projectIds, userIds});
  }

  getPartnerPurchaseOrders(projectId = ""): Promise<any> {
    return this.http.get(`${this.url}/getPartnerPurchaseOrders?id=${projectId}`)
      .toPromise();
  }

  purge(projectId: string = "",message): Promise<boolean> {
    return this.http.post<{success: boolean}>(`${this.url}/purge`, {projectId,message})
      .pipe(map(result => result.success))
      .toPromise();
  }

  rejectProject(projectId: string, reason: string, workflowId: string): Promise<{success: boolean, message: string}> {
    const params = {projectId, reason, workflowId};

    return this.http.post<{success: boolean, message: string}>(`${this.url}/reject-project`, params)
      .pipe(map(({success, message}) => ({success, message})))
      .toPromise();
  }

  unRejectProject(projectId: string): Promise<{success: boolean, message: string}> {
    const params = {projectId};

    return this.http.post<{success: boolean, message: string}>(`${this.url}/un-reject-project`, params)
      .pipe(map(({success, message}) => ({success, message})))
      .toPromise();
  }

  saveSiteNameStructure(projectId: string, siteNameStructure: string): Promise<void> {
    const params = {projectId, siteNameStructure};
    return this.http.post<void>(`${this.url}/save-site-name-structure`, params).toPromise();
  }

  exportMasterAgreement(projectId): Promise<{success: boolean, message: string, currentStatus: any}> {
    return this.http.post<{success: boolean, message: string, currentStatus: MasterAgreementStatus}>(
      `${this.url}/export-master-agreement`,
      {projectId},
    )
      .pipe(map(({success, message, currentStatus}) => ({success, message, currentStatus})))
      .toPromise();
  }

  appendMasterAgreement(projectId, startDate): Promise<{success: boolean, message: string, currentStatus: any}> {
    return this.http.post<{success: boolean, message: string, currentStatus: MasterAgreementStatus}>(
      `${this.url}/append-master-agreement`,
      {projectId, startDate},
    )
      .pipe(map(({success, message, currentStatus}) => ({success, message, currentStatus})))
      .toPromise();
  }

  releaseMasterAgreement(
    projectId,
  ): Promise<{success: boolean, message: string, currentStatus: MasterAgreementStatus}> {
    return this.http.post<{success: boolean, message: string, currentStatus: MasterAgreementStatus}>(
      `${this.url}/release-master-agreement`,
      {projectId},
    )
      .pipe(map(({success, message, currentStatus}) => ({success, message, currentStatus})))
      .toPromise();
  }

  getMasterAgreementStatus(projectIdInput): Promise<MasterAgreementStatus> {
    return this.http.post<MasterAgreementStatus>(`${this.url}/get-master-agreement-status`, {projectId: projectIdInput})
      .pipe(map(({
        projectId,
        currentMaStatus,
        projectStatus,
        lastMasterAgreementReturnStatus,
        currentMaInternalId,
        externalId,
        opportunityId,
        maStartDate,
        custPoItemNo,
        masterAgreementDescription,
        submittedTs,
        submittedBy,
        pendingMaTransactionId,
        itsmCustomerName,
        isTemplateDartImport,
        itsmCustomerId,
        hasHardErrors,
        validationErrors,
        productsNeedingExport,
        independentServicesNeedingOrders,
        solutions,
        isAppend,
      }) => ({
        projectId,
        currentMaStatus,
        projectStatus,
        lastMasterAgreementReturnStatus,
        currentMaInternalId,
        externalId,
        opportunityId,
        maStartDate,
        custPoItemNo,
        masterAgreementDescription,
        submittedTs,
        submittedBy,
        pendingMaTransactionId,
        itsmCustomerName,
        isTemplateDartImport,
        itsmCustomerId,
        hasHardErrors,
        validationErrors,
        productsNeedingExport,
        independentServicesNeedingOrders,
        solutions,
        isAppend,
      })))
      .toPromise();
  }

  abandonMasterAgreementTransaction(projectId): Promise<{success: boolean, message: string, currentStatus: any}> {
    return this.http.post<{success: boolean, message: string, currentStatus: any}>(
      `${this.url}/abandon-master-agreement-transaction`,
      {projectId},
    )
      .pipe(map(({success, message, currentStatus}) => ({success, message, currentStatus})))
      .toPromise();
  }

  manuallyAddMasterAgreementTransaction(projectId, internalId):
    Promise<{success: boolean, message: string, currentStatus: any}> {
    return this.http.post<{success: boolean, message: string, currentStatus: any}>(
      `${this.url}/manually-add-master-agreement`,
      {projectId, internalId},
    )
      .pipe(map(({success, message, currentStatus}) => ({success, message, currentStatus})))
      .toPromise();
  }

  manuallySendProjectLevelOnboarding(projectId): Promise<{success: boolean, message: string, currentStatus: any}> {
    return this.http.post<{success: boolean, message: string, currentStatus: any}>(
      `${this.url}/manually-onboard-project`,
      {projectId},
    )
      .pipe(map(({success, message, currentStatus}) => ({success, message, currentStatus})))
      .toPromise();
  }

  debugEmulateProjectLevelOnboarding(projectId): Promise<{success: boolean, message: string, currentStatus: any}> {
    return this.http.post<{success: boolean, message: string, currentStatus: any}>(
      `${this.url}/debug-emulate-onboarding`,
      {projectId},
    )
      .pipe(map(({success, message, currentStatus}) => ({success, message, currentStatus})))
      .toPromise();
  }

  sendDevIndServPoRequests(projectId, deviceId?: string):
    Promise<{success: boolean, message: string, currentStatus: any}> {
    return this.http.post<{success: boolean, message: string, currentStatus: any}>(
      `${this.url}/send-dev-ind-ser-po-req`,
      {projectId, deviceId},
    )
      .pipe(map(({success, message, currentStatus}) => ({success, message, currentStatus})))
      .toPromise();
  }

  clearDevIndServPoStatus(projectId, deviceId): Promise<{success: boolean, message: string, currentStatus: any}> {
    return this.http.post<{success: boolean, message: string, currentStatus: any}>(
      `${this.url}/clear-dev-ind-ser-po-req`,
      {projectId, deviceId},
    )
      .pipe(map(({success, message, currentStatus}) => ({success, message, currentStatus})))
      .toPromise();
  }

  timeoutPendingPoRequests(projectId, deviceId): Promise<{success: boolean, message: string, currentStatus: any}> {
    return this.http.post<{success: boolean, message: string, currentStatus: any}>(
      `${this.url}/timeout-dev-ind-ser-po-req`,
      {projectId, deviceId},
    )
      .pipe(map(({success, message, currentStatus}) => ({success, message, currentStatus})))
      .toPromise();
  }

  onBoardCustomer(projectId): Promise<any> {
    return this.http.post(`${this.url}/on-board-customer`, {projectId})
      .toPromise();
  }

  listenToTransactionStatus(projectId, transactionId): Observable<TransactionResponse> {
    return this.http.post<TransactionResponse>(`${this.url}/listen-to-transaction`, {projectId, transactionId});
  }

  getProjectCountry(projectId: string): Promise<any> {
    return this.http.get(`${this.url}/project-country?id=${projectId}`)
      .toPromise();
  }

  getItsmContacts(projectId: string, forDeployment: boolean = false, checkContactActiveStatus: boolean = false): Promise<ItsmContactModel[]> {
    return this.http.get(`${this.url}/get-itsm-contacts?projectId=${projectId}&forDeployment=${forDeployment}&checkContactActiveStatus=${checkContactActiveStatus}`)
      .pipe(map(itsmContactModelListFactory))
      .toPromise();
  }

  getItsmAddresses(projectId: string): Promise<ItsmAddressesForProjectModel> {
    return this.http.get(`${this.url}/get-itsm-addresses?projectId=${projectId}`)
      .pipe(map(itsmAddressesForProjectModelFactory))
      .toPromise();
  }

  getFedlLLCAllowedProject(projectId): Promise<any> {
    return this.http.get<any>(`${this.url}/project-allows-fedllc?projectId=${projectId}`)
      .pipe(map(json => json.projectAllowsFedLLC))
      .toPromise();
  }

  setMs4ItsmAddress(projectId, isReseller, selectedCustomerInfo, isMs4Refresh = false): Promise<any> {
    return this.http.post(`${this.url}/set-ms4-customer`, {projectId, isReseller, selectedCustomerInfo, isMs4Refresh})
      .toPromise();
  }

  getAllITSMCountries(verbose = false): Promise<any> {
    return this.http.get<any>(`${this.url}/itsm-countries?verbose=${verbose}`)
      .toPromise();
  }

  getITSMCountriesByGeoRegion(geoRegion): Promise<any> {
    return this.http.get<any>(`${this.url}/itsm-countries?geoRegion=${geoRegion}`)
      .toPromise();
  }

  getAllITSMRegions(): Promise<any> {
    return this.http.get<any>(`${this.url}/itsm-regions`)
      .toPromise();
  }

  getITSMRegionsByCountryId(countryId): Promise<any> {
    return this.http.get<any>(`${this.url}/itsm-regions?countryId=${countryId}`)
      .toPromise();
  }
  
  getITSMAccountsDetails(projectId): Promise<any> {
    return this.http.get(`${this.url}/get-itsm-account?projectId=${projectId}`)
      .toPromise();
  }
  getInvoiceCurrency(currency,projectId): Promise<any> {
    const params = {currency,projectId};
    return this.http.get(`${this.url}/get-invoice-currency`, {params}).toPromise();
  }

  saveContractAccountsAssociation(projectId, contractAccounts, opportunityId): Promise<any> {
    return this.http.post(`${this.url}/save-contract-accounts-associations`, {projectId, contractAccounts, opportunityId})
      .pipe(map(json => json))
      .toPromise();
  }

  async getCountryByRegionId(geoRegionId: string, projectId?: string): Promise<any> {
    Query.COUNTRY_BY_GEO_REGION.variables.geoRegionId = geoRegionId;
    const params: { query: any, uiAction: string, projectId?: string } = {
      query: Query.COUNTRY_BY_GEO_REGION,
      uiAction: "CREATE-PROJECT",
    };
    if (projectId) params.projectId = projectId;
    const countryListbyGeoRegion = await this.hasuraGqlService.getResponseFromHasura(params);
    if (countryListbyGeoRegion && countryListbyGeoRegion.body && countryListbyGeoRegion.body.data &&
      countryListbyGeoRegion.body.data.country &&
      countryListbyGeoRegion.body.data.country.length === 0) {
      throw new Error("No countries found for the selected region");
    }
    const sortedCountryList = countryListbyGeoRegion.body.data.country.sort((a, b) => {
      if (a.country_name < b.country_name) return -1;
      if (a.country_name > b.country_name) return 1;
      return 0;
    });
    return sortedCountryList;
  }
}
